<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Mouvements de stock ({{ totalRows }})</h1>
          <small class="text-muted">Entrées, sorties, pertes, retours et ajustements.</small>
        </div>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['stock_movements', 'StockMovement']" label="Mouvements de stock" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>
          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouveau mouvement</span>
          </button>
          <router-link :to="{ name: 'MovementSettings' }" class="btn btn-outline-primary btn-sm btn-icon-label">
            <i class="fa fa-sliders"></i>
            <span class="btn-label ms-1">Motifs</span>
          </router-link>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">{{ successMessage }}</div>

      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-lg-2 col-md-6">
              <label class="form-label small mb-1">Type</label>
              <PrimevueDropdown
                v-model="filters.type"
                :options="movementTypeFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>

            <div class="col-lg-3 col-md-6">
              <label class="form-label small mb-1">Motif</label>
              <PrimevueDropdown
                v-model="filters.stock_movement_reason_id"
                :options="reasonFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
                filter
              />
            </div>

            <div class="col-lg-3 col-md-6">
              <label class="form-label small mb-1">Produit</label>
              <PrimevueDropdown
                v-model="filters.product_id"
                :options="productFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
                filter
              />
            </div>

            <div class="col-lg-2 col-md-6">
              <label class="form-label small mb-1">Du</label>
              <input v-model="filters.date_from" type="date" class="form-control form-control-sm" />
            </div>

            <div class="col-lg-2 col-md-6">
              <label class="form-label small mb-1">Au</label>
              <input v-model="filters.date_to" type="date" class="form-control form-control-sm" />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchMovements">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="movementsResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="stock-movements.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher lot, source ou note..."
        empty-text="Aucun mouvement trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchMovements"
      >
        <template #cell-occurred_at="{ row }">
          {{ $format.datetime(row.occurred_at) }}
        </template>

        <template #cell-type="{ row }">
          <span class="badge" :class="typeClass(row.type)">
            {{ typeLabel(row.type) }}
          </span>
        </template>

        <template #cell-product="{ row }">
          <div>
            <div class="fw-semibold">{{ row.product?.name || '-' }}</div>
            <small class="text-muted">{{ row.product?.sku || '' }}</small>
          </div>
        </template>

        <template #cell-signed_quantity="{ row }">
          <span :class="row.signed_quantity >= 0 ? 'text-success' : 'text-danger'">
            {{ row.signed_quantity >= 0 ? '+' : '' }}{{ row.signed_quantity }}
          </span>
        </template>

        <template #cell-stock="{ row }">
          {{ row.stock_before ?? '-' }} → {{ row.stock_after ?? '-' }}
        </template>

        <template #cell-source_type="{ row }">
          {{ sourceLabel(row.source_type) }}
        </template>

        <template #cell-reason="{ row }">
          <div v-if="row.reason">
            <div class="fw-semibold">{{ row.reason.label }}</div>
            <small class="text-muted">{{ reasonTypeLabel(row.reason.type) }}</small>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell-lot_number="{ row }">
          {{ row.lot_number || '-' }}
        </template>

        <template #cell-expiry_date="{ row }">
          {{ row.expiry_date ? $format.date(row.expiry_date) : '-' }}
        </template>

        <template #cell-user="{ row }">
          {{ row.user?.name || '-' }}
        </template>
      </BootstrapDataTable>

      <PrimevueDialog
        v-model:visible="dialogVisible"
        :modal="true"
        :closable="false"
        :style="{ width: '680px', maxWidth: '96vw' }"
      >
        <template #header>
          <h5 class="modal-title mb-0">Nouveau mouvement de stock</h5>
        </template>

        <form @submit.prevent="submitMovement">
          <div class="row g-3">
            <div class="col-md-7">
              <label class="form-label">Produit</label>
              <PrimevueDropdown
                v-model="form.product_id"
                :options="productFormOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Sélectionner un produit"
                class="w-100"
                filter
              />
            </div>

            <div class="col-md-5">
              <label class="form-label">Motif</label>
              <PrimevueDropdown
                v-model="form.stock_movement_reason_id"
                :options="reasonFormOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Sélectionner un motif"
                class="w-100"
                filter
                required
              />
              <small class="text-muted">
                Le motif définit automatiquement si le mouvement est une entrée ou une sortie.
              </small>
            </div>

            <div class="col-md-4">
              <label class="form-label">Quantité</label>
              <PrimevueInputNumber v-model="form.quantity" class="w-100" :min="1" />
            </div>

            <div class="col-md-4">
              <label class="form-label">Prix unitaire</label>
              <PrimevueInputNumber v-model="form.unit_price" class="w-100" mode="decimal" :min="0" :maxFractionDigits="2" />
            </div>

            <div class="col-md-4">
              <label class="form-label">Date</label>
              <input v-model="form.occurred_at" type="datetime-local" class="form-control" />
            </div>

            <div class="col-md-6">
              <label class="form-label">Lot</label>
              <PrimevueInputText v-model="form.lot_number" class="w-100" placeholder="Ex: LOT-2026-05" />
            </div>

            <div class="col-md-6">
              <label class="form-label">Date d'expiration</label>
              <input v-model="form.expiry_date" type="date" class="form-control" />
            </div>

            <div class="col-12">
              <label class="form-label">Note</label>
              <PrimevueTextarea v-model="form.notes" class="w-100" rows="3" autoResize placeholder="Motif ou précision" />
            </div>
          </div>

          <div v-if="formError" class="alert alert-danger py-2 mt-3">{{ formError }}</div>

          <div class="d-flex justify-content-end gap-2 mt-4">
            <button type="button" class="btn btn-outline-secondary btn-icon-label" :disabled="isSaving" @click="closeDialog">
              <i class="fa fa-xmark"></i>
              <span class="btn-label ms-1">Annuler</span>
            </button>
            <AppLoadingButton
              type="submit"
              class="btn btn-primary btn-icon-label"
              :loading="isSaving"
              :disabled="!form.stock_movement_reason_id"
              loading-label="Enregistrement..."
            >
              <i class="fa fa-check"></i>
              <span class="btn-label ms-1">Enregistrer</span>
            </AppLoadingButton>
          </div>
        </form>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import { defineComponent } from 'vue';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';

const FILTER_MODULE = 'stock_movements';
const DEFAULT_FILTERS = {
  search: '',
  type: '',
  stock_movement_reason_id: '',
  product_id: '',
  date_from: '',
  date_to: '',
};

const DEFAULT_FORM = {
  product_id: '',
  stock_movement_reason_id: '',
  quantity: 1,
  unit_price: null,
  lot_number: '',
  expiry_date: '',
  occurred_at: '',
  notes: '',
};

export default defineComponent({
  name: 'StockMovementsView',
  data() {
    return {
      movementsResponse: null,
      products: [],
      reasons: [],
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      table: {
        sortBy: 'occurred_at',
        sortDir: 'desc',
        page: 1,
        perPage: 20,
      },
      tableColumns: [
        { key: 'occurred_at', label: 'Date', sortable: true },
        { key: 'type', label: 'Type', sortable: true, class: 'text-center' },
        { key: 'product', label: 'Produit' },
        { key: 'signed_quantity', label: 'Qté', sortable: true, class: 'text-center' },
        { key: 'stock', label: 'Stock', class: 'text-center' },
        { key: 'reason', label: 'Motif' },
        { key: 'source_type', label: 'Source', sortable: true },
        { key: 'lot_number', label: 'Lot' },
        { key: 'expiry_date', label: 'Expiration' },
        { key: 'user', label: 'Utilisateur' },
      ],
      movementTypes: [
        { value: 'entry', label: 'Entrée' },
        { value: 'exit', label: 'Sortie' },
      ],
      dialogVisible: false,
      form: { ...DEFAULT_FORM },
      formError: '',
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
    };
  },
  mounted() {
    this.restoreFilters();
    this.fetchProducts();
    this.fetchReasons();
    this.fetchMovements();
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },
    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },
    async fetchProducts() {
      try {
        const res = await this.$api.get('/products', { params: { limit: 500, sort_by: 'name', sort_dir: 'asc' } });
        this.products = res.data.data || [];
      } catch (error) {
        console.error('Erreur produits mouvements', error);
      }
    },
    async fetchReasons() {
      try {
        const res = await this.$api.get('/stock-movement-reasons');
        this.reasons = res.data.data || [];
      } catch (error) {
        console.error('Erreur motifs mouvements', error);
      }
    },
    async fetchMovements() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const params = {
          ...cleanFilterParams(this.filters),
          paginate: 1,
          per_page: this.table.perPage,
          page: this.table.page,
          sort_by: this.table.sortBy,
          sort_dir: this.table.sortDir,
        };

        const res = await this.$api.get('/stock-movements', { params });
        this.movementsResponse = res.data;
      } catch (error) {
        console.error('Erreur mouvements stock', error);
        this.errorMessage = 'Impossible de charger les mouvements de stock.';
      } finally {
        this.isLoading = false;
      }
    },
    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchMovements();
    },
    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchMovements();
    },
    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchMovements();
    },
    handlePageChange(page) {
      this.table.page = page;
      this.fetchMovements();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchMovements();
    },
    openCreateDialog() {
      this.form = { ...DEFAULT_FORM };
      this.formError = '';
      this.dialogVisible = true;
    },
    closeDialog() {
      this.dialogVisible = false;
    },
    async submitMovement() {
      if (this.isSaving) return;

      if (!this.form.stock_movement_reason_id) {
        this.formError = 'Sélectionnez un motif pour enregistrer le mouvement.';
        return;
      }

      this.isSaving = true;
      this.formError = '';
      this.successMessage = '';

      try {
        const payload = {
          ...this.form,
          stock_movement_reason_id: this.form.stock_movement_reason_id || null,
          unit_price: this.form.unit_price === '' ? null : this.form.unit_price,
          lot_number: this.form.lot_number || null,
          expiry_date: this.form.expiry_date || null,
          occurred_at: this.form.occurred_at || null,
          notes: this.form.notes || null,
        };

        const res = await this.$api.post('/stock-movements', payload);
        this.successMessage = res.data.message || 'Mouvement enregistré.';
        this.dialogVisible = false;
        await this.fetchProducts();
        await this.fetchReasons();
        await this.fetchMovements();
      } catch (error) {
        console.error('Erreur création mouvement', error);
        this.formError = error?.response?.data?.message || "Impossible d'enregistrer le mouvement.";
      } finally {
        this.isSaving = false;
      }
    },
    typeLabel(type) {
      return this.movementTypes.find((item) => item.value === type)?.label || type || '-';
    },
    reasonTypeLabel(type) {
      return {
        system: 'Système',
        standard: 'Standard',
      }[type] || type || '-';
    },
    typeClass(type) {
      return {
        entry: 'bg-success',
        exit: 'bg-danger',
      }[type] || 'bg-light text-dark';
    },
    sourceLabel(source) {
      return {
        manual: 'Manuel',
        purchase: 'Achat',
        sale: 'Vente',
        sale_cancellation: 'Annulation vente',
        supplier_return: 'Retour fournisseur',
        inventory: 'Inventaire',
        stock_transfer_out: 'Transfert envoyé',
        stock_transfer_in: 'Transfert reçu',
        stock_transfer_cancellation: 'Annulation transfert',
      }[source] || source || '-';
    },
  },
  computed: {
    totalRows() {
      const fallback = Array.isArray(this.movementsResponse?.data)
        ? this.movementsResponse.data.length
        : 0;
      return extractApiTotal(this.movementsResponse, fallback);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    activeStandardReasons() {
      return this.reasons.filter((reason) => reason.is_manual_selectable && reason.is_active);
    },
    movementTypeFilterOptions() {
      return [{ value: '', label: 'Tous' }, ...this.movementTypes];
    },
    reasonFilterOptions() {
      return [
        { value: '', label: 'Tous' },
        ...this.reasons.map((reason) => ({ value: String(reason.id), label: reason.label })),
      ];
    },
    productFilterOptions() {
      return [
        { value: '', label: 'Tous' },
        ...this.products.map((product) => ({ value: String(product.id), label: product.name })),
      ];
    },
    productFormOptions() {
      return this.products.map((product) => ({
        value: product.id,
        label: `${product.name} - stock ${product.stock_quantity}`,
      }));
    },
    reasonFormOptions() {
      return this.activeStandardReasons.map((reason) => ({
        value: reason.id,
        label: `${reason.label} - ${this.typeLabel(reason.impact)}`,
      }));
    },
  },
});
</script>
